import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Inbox,
  Search,
  Loader2,
  Mail,
  Building2,
  Phone,
  Archive,
  CornerUpLeft,
  Eye
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const STATUS_STYLES = {
  new: 'bg-blue-100 text-blue-600 border-blue-500/30',
  read: 'bg-gray-100 text-gray-600 border-gray-200',
  replied: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  archived: 'bg-amber-50 text-amber-700 border-amber-200'
};

const PAGE_SIZE = 50;

function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [total, setTotal] = useState(0);
  const [matching, setMatching] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(function () {
    setLoading(true);
    loadMessages(0, false);
  }, [statusFilter]);

  async function loadMessages(skip, append) {
    try {
      const params = { limit: PAGE_SIZE, skip: skip };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await api.get('/admin/contact-messages', { params: params });
      setMessages(function (prev) {
        return append ? prev.concat(res.data.messages) : res.data.messages;
      });
      setUnread(res.data.unread);
      setTotal(res.data.total);
      setMatching(res.data.matching);
    } catch (err) {
      console.error('Failed to load messages:', err);
      toast.error('Failed to load enquiries');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  function loadMore() {
    setLoadingMore(true);
    loadMessages(messages.length, true);
  }

  async function updateStatus(messageId, status) {
    setUpdatingId(messageId);
    try {
      const res = await api.put('/admin/contact-messages/' + messageId + '/status', { status });
      setMessages(function (prev) {
        return prev.map(function (m) { return m.id === messageId ? res.data : m; });
      });
      setUnread(function (prev) { return status === 'new' ? prev + 1 : Math.max(0, prev - 1); });
      toast.success('Marked as ' + status);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update enquiry');
    } finally {
      setUpdatingId(null);
    }
  }

  function toggleExpand(message) {
    const opening = expandedId !== message.id;
    setExpandedId(opening ? message.id : null);
    if (opening && message.status === 'new') {
      updateStatus(message.id, 'read');
    }
  }

  const filtered = messages.filter(function (m) {
    const haystack = [m.name, m.email, m.subject, m.company].join(' ').toLowerCase();
    return haystack.includes(search.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="messages-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-messages">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[32px] sv-display text-ink">Customer enquiries</h1>
          <p className="text-gray-500 mt-1">Messages submitted through the public contact form</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="px-5 py-3 rounded-sm bg-white shadow-card" data-testid="messages-total">
            <p className="text-xs text-gray-400">Total</p>
            <p className="text-2xl font-normal tracking-[-0.01em] text-ink">{total}</p>
          </div>
          <div className="px-5 py-3 rounded-sm bg-ink text-white shadow-card" data-testid="messages-unread">
            <p className="text-xs text-white/60">Unread</p>
            <p className="text-2xl font-normal tracking-[-0.01em]">{unread}</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            value={search}
            onChange={function (e) { setSearch(e.target.value); }}
            placeholder="Search by name, email, company or subject"
            className="h-14 pl-12 rounded-sm"
            data-testid="messages-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-14 w-full sm:w-56 rounded-sm" data-testid="messages-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="read">Read</SelectItem>
            <SelectItem value="replied">Replied</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-sm shadow-card p-16 text-center" data-testid="messages-empty">
          <Inbox className="h-16 w-16 text-gray-300 mx-auto mb-6" />
          <h2 className="text-2xl font-normal tracking-[-0.01em] text-ink mb-2">No enquiries found</h2>
          <p className="text-gray-500">Nothing matches your current search and filter.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map(function (message) {
            const isOpen = expandedId === message.id;
            return (
              <div
                key={message.id}
                className="bg-white rounded-sm shadow-card overflow-hidden transition-shadow"
                data-testid="message-row"
              >
                <button
                  type="button"
                  onClick={function () { toggleExpand(message); }}
                  className="w-full text-left p-6 flex flex-wrap items-start justify-between gap-4"
                  data-testid={'message-toggle-' + message.id}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <p className="font-medium text-ink">{message.subject}</p>
                      <Badge variant="outline" className={STATUS_STYLES[message.status] || STATUS_STYLES.read}>
                        {message.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 flex-wrap">
                      <span className="font-medium text-gray-700">{message.name}</span>
                      <span className="flex items-center gap-1"><Mail className="h-4 w-4" />{message.email}</span>
                      {message.company && (
                        <span className="flex items-center gap-1"><Building2 className="h-4 w-4" />{message.company}</span>
                      )}
                      {message.phone && (
                        <span className="flex items-center gap-1"><Phone className="h-4 w-4" />{message.phone}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right text-sm text-gray-400 shrink-0">
                    {message.created_at ? format(parseISO(message.created_at), 'MMM d, yyyy HH:mm') : '-'}
                    <span className="flex items-center gap-1 justify-end mt-1 text-blue-600">
                      <Eye className="h-4 w-4" />{isOpen ? 'Hide' : 'View'}
                    </span>
                  </div>
                </button>

                {isOpen && (
                  <div className="px-6 pb-6 border-t border-gray-100 pt-5" data-testid={'message-body-' + message.id}>
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{message.message}</p>
                    <div className="flex flex-wrap gap-3 mt-6">
                      <a href={'mailto:' + message.email + '?subject=Re: ' + encodeURIComponent(message.subject)}>
                        <Button className="rounded-sm bg-ink hover:bg-ink-800" data-testid={'message-reply-' + message.id}>
                          <CornerUpLeft className="mr-2 h-4 w-4" /> Reply by email
                        </Button>
                      </a>
                      <Button
                        variant="outline"
                        className="rounded-full"
                        disabled={updatingId === message.id || message.status === 'replied'}
                        onClick={function () { updateStatus(message.id, 'replied'); }}
                        data-testid={'message-mark-replied-' + message.id}
                      >
                        Mark as replied
                      </Button>
                      <Button
                        variant="outline"
                        className="rounded-full"
                        disabled={updatingId === message.id || message.status === 'archived'}
                        onClick={function () { updateStatus(message.id, 'archived'); }}
                        data-testid={'message-archive-' + message.id}
                      >
                        <Archive className="mr-2 h-4 w-4" /> Archive
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {messages.length < matching && (
            <div className="text-center pt-2">
              <Button
                variant="outline"
                className="rounded-full px-8"
                onClick={loadMore}
                disabled={loadingMore}
                data-testid="messages-load-more"
              >
                {loadingMore && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Load more ({messages.length} of {matching})
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminMessages;
